import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useCompany } from '../../contexts/CompanyContext';
import LoadingSpinner from '../common/LoadingSpinner';

const ProtectedRoute = ({ children, requiredRole = null, requiredPermission = null }) => {
  const { isAuthenticated, user, loading, hasRole, canAccess } = useAuth();
  const { getSubscriptionStatus } = useCompany();
  const location = useLocation();

  // Show loading spinner while auth state is being determined
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <LoadingSpinner size="large" text="Loading..." />
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check role requirements
  if (requiredRole && !hasRole(requiredRole)) {
    // Redirect based on user's actual role
    switch (user.role) {
      case 'super_admin':
        return <Navigate to="/super-admin" replace />;
      case 'company_admin':
        return <Navigate to="/company-admin" replace />;
      case 'user':
        return <Navigate to="/user-dashboard" replace />;
      default:
        return <Navigate to="/dashboard" replace />;
    }
  }

  // Check permission requirements
  if (requiredPermission && !canAccess(requiredPermission)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
            <svg
              className="h-6 w-6 text-red-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <h3 className="mt-2 text-sm font-medium text-gray-900">Access Denied</h3>
          <p className="mt-1 text-sm text-gray-500">
            You don't have permission to access this feature.
          </p>
          <div className="mt-6">
            <button
              onClick={() => window.history.back()}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Check subscription status - allow access to dashboard pages for payment renewal
  const subscriptionStatus = getSubscriptionStatus();
  const isExpired = subscriptionStatus === 'expired';

  // Allow super admins full access
  if (user?.role === 'super_admin') {
    return children;
  }

  // For expired subscriptions, only allow access to dashboard pages where users can renew
  if (isExpired) {
    const allowedPaths = ['/dashboard', '/company-admin', '/user-dashboard'];
    const currentPath = location.pathname;

    // Check if current path is allowed for renewal
    const isAllowedPath = allowedPaths.some(allowedPath => {
      if (allowedPath === '/dashboard' && currentPath === '/') return true; // Root redirects to dashboard
      return currentPath.startsWith(allowedPath);
    });

    if (!isAllowedPath) {
      // Block access to CRM functionality but allow dashboard access for renewal
      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-black p-4">
          <div className="max-w-md w-full bg-gray-800 rounded-3xl shadow-2xl border border-gray-700 p-8 text-center">
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-red-500/20 backdrop-blur-sm rounded-full shadow-2xl">
                <svg
                  className="h-12 w-12 text-red-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-white mb-4">
              Subscription Expired
            </h2>

            <p className="text-gray-300 mb-6 leading-relaxed">
              Your trial period has ended and your subscription has expired. To continue using the CRM platform, please renew your subscription.
            </p>

            <div className="space-y-3 mb-6">
              <div className="flex items-center justify-center space-x-2 text-gray-400 text-sm">
                <svg className="h-4 w-4 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                <span>Access to CRM features blocked</span>
              </div>
              <div className="flex items-center justify-center space-x-2 text-gray-400 text-sm">
                <svg className="h-4 w-4 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                <span>Lead and customer management disabled</span>
              </div>
            </div>

            <div className="space-y-3">
              {user?.role === 'company_admin' ? (
                <button
                  onClick={() => window.location.href = '/company-admin'}
                  className="w-full inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white text-lg font-semibold rounded-2xl shadow-2xl hover:shadow-red-500/25 transition-all duration-300"
                >
                  <svg className="h-6 w-6 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                  </svg>
                  Go to Dashboard & Renew
                </button>
              ) : (
                <button
                  onClick={() => window.location.href = '/dashboard'}
                  className="w-full inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white text-lg font-semibold rounded-2xl shadow-2xl hover:shadow-red-500/25 transition-all duration-300"
                >
                  <svg className="h-6 w-6 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                  </svg>
                  Go to Dashboard
                </button>
              )}

              <p className="text-xs text-gray-500 mt-4">
                Contact your company administrator to renew the subscription.
              </p>
            </div>
          </div>
        </div>
      );
    }
  }

  return children;
};

export default ProtectedRoute;
